import type { ReactElement } from 'react';
import type { ProjectDataTableItem } from '@kirin/api-client/contracts/projects';
import { TableCell, TableRow } from '@/components/ui/table';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { InventoryUserListDialog } from './InventoryUserListDialog';

interface ProjectUserListModalProps {
  /** The selected project row, or null when the modal is closed. */
  project: ProjectDataTableItem | null;
  onClose: () => void;
}

/**
 * Users who have a specific project. Unlike the MCP / Instructions modals this
 * fetches nothing — the projects DataTable row already carries the full `users`
 * array, so we render straight from it (no loading/error states).
 */
export function ProjectUserListModal({
  project,
  onClose,
}: ProjectUserListModalProps): ReactElement {
  const users = project?.users ?? [];

  return (
    <InventoryUserListDialog
      open={project !== null}
      onClose={onClose}
      title={
        <>
          Users with <span className="font-medium">{project?.name}</span>
        </>
      }
      columns={[{ label: 'User' }, { label: 'Last Seen' }]}
      isLoading={false}
      error={null}
      isEmpty={users.length === 0}
      emptyMessage="No users found for this project"
    >
      {users.map(user => (
        <TableRow key={user.userId}>
          <TableCell>
            <div className="font-medium">{user.name || 'Unknown User'}</div>
            {user.email && (
              <div className="text-sm text-muted-foreground break-all">
                {user.email}
              </div>
            )}
          </TableCell>
          <TableCell className="text-muted-foreground">
            <RelativeTimeWithTooltip date={user.lastSeenAt} />
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}
